import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { apiRequest } from '../../utils/apiClient';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import ResultCard from '../search/components/ResultCard';
import { toFrontendProperty, type SearchProperty } from '../../services/ragSearchService';
import type { Property } from '../../mocks/properties';
import DeveloperProjectCard, { type DeveloperProject } from './DeveloperProjectCard';

const PAGE_SIZE = 24;

interface FalLicense {
  licenseType?: string | null;
  licenseNumber?: string | null;
  status?: string | null;
  expiryDate?: string | null;
  statusFullAr?: string | null;
}

interface PublicDeveloper {
  id: string;
  name: string;
  nameAr?: string | null;
  logoUrl?: string | null;
  coverUrl?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  headquartersCity?: string | null;
  region?: string | null;
  regionAr?: string | null;
  foundedYear?: number | null;
  licenseNumber?: string | null;
  isVerified: boolean;
  avgRating: number;
  totalReviews: number;
  activeListingsCount: number;
  projectsCount: number;
  openForInvestmentCount: number;
  projectsCompleted: number;
  falLicense?: FalLicense | null;
  falVerified?: boolean;
  createdAt?: string;
}

interface ListMeta { totalItems: number; totalPages: number }

export default function DeveloperProfilePage() {
  const { i18n } = useTranslation(['common']);
  const isRTL = i18n.dir() === 'rtl';
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [developer, setDeveloper] = useState<PublicDeveloper | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [projects, setProjects] = useState<DeveloperProject[]>([]);
  const [listings, setListings] = useState<Property[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listingsLoading, setListingsLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    apiRequest<{ data: PublicDeveloper }>(`/developers/${slug}`)
      .then((res) => setDeveloper(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    apiRequest<{ data: DeveloperProject[] }>(`/developers/${slug}/projects`)
      .then((res) => setProjects(res.data ?? []))
      .catch(() => setProjects([]));
  }, [slug]);

  const fetchListings = useCallback(async (developerId: string, p: number) => {
    setListingsLoading(true);
    try {
      const res = await apiRequest<{ data: SearchProperty[]; meta?: ListMeta }>(
        `/properties?developerId=${developerId}&page=${p}&pageSize=${PAGE_SIZE}`,
      );
      setListings((res.data ?? []).map(toFrontendProperty) as Property[]);
      setTotalPages(res.meta?.totalPages ?? 1);
    } catch {
      setListings([]);
      setTotalPages(1);
    } finally {
      setListingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (developer?.id) fetchListings(developer.id, page);
  }, [developer?.id, page, fetchListings]);

  const goToPage = (target: number) => {
    const next = Math.min(Math.max(1, target), totalPages);
    if (next !== page) { setPage(next); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !developer) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] mb-4">
            {isRTL ? 'المطوّر غير موجود' : 'Developer not found'}
          </h1>
          <Link to="/trusted-brokers">
            <Button variant="primary">{isRTL ? 'تصفح المطورين' : 'Browse developers'}</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const name = isRTL ? (developer.nameAr || developer.name) : developer.name;
  const description = isRTL ? (developer.descriptionAr || developer.description) : developer.description;
  const city = isRTL ? (developer.region || developer.headquartersCity) : (developer.headquartersCity || developer.region);
  const waPhone = (developer.phone || '').replace(/[^0-9]/g, '');
  const fal = developer.falLicense;

  const stats = [
    { label: isRTL ? 'مشاريع' : 'Projects', value: developer.projectsCount, icon: 'ri-building-4-line' },
    { label: isRTL ? 'وحدات معروضة' : 'Listings', value: developer.activeListingsCount, icon: 'ri-home-4-line' },
    { label: isRTL ? 'فرص استثمار' : 'Open to invest', value: developer.openForInvestmentCount, icon: 'ri-funds-line' },
    { label: isRTL ? 'التقييم' : 'Rating', value: developer.totalReviews > 0 ? developer.avgRating.toFixed(1) : '-', icon: 'ri-star-line' },
  ];

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={i18n.dir()}>
      <Navigation />
      <main className="pb-20">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Header card */}
          <section className="rounded-3xl bg-white border border-slate-200/80 overflow-hidden mb-4 shadow-sm">
            <div
              className="h-28 bg-[#004D40] bg-cover bg-center"
              style={developer.coverUrl ? { backgroundImage: `url(${developer.coverUrl})` } : undefined}
            />
            <div className="px-5 sm:px-8 pb-6">
              <div className={`flex flex-col sm:flex-row sm:items-start gap-4 ${isRTL ? 'sm:flex-row-reverse sm:text-right' : ''}`}>
                {/* Logo — only the logo overlaps the cover (its white ring keeps it legible);
                    the name/meta stay on the white card below so dark text never sits on the image. */}
                <div className="shrink-0 -mt-14">
                  {developer.logoUrl ? (
                    <img src={developer.logoUrl} alt={name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl ring-4 ring-white shadow-md object-cover bg-white" />
                  ) : (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl ring-4 ring-white shadow-md bg-[#004D40] flex items-center justify-center">
                      <span className="text-white text-3xl font-bold">{name.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0 pt-1 sm:pt-4">
                  <div className={`flex items-center gap-2 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <h1 className="text-xl sm:text-2xl font-bold text-[var(--pv-color-primary)] truncate">{name}</h1>
                    {developer.isVerified && <i className="ri-verified-badge-fill text-[#004D40] text-xl" title={isRTL ? 'موثّق' : 'Verified'} />}
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-[11px] font-medium">
                      <i className="ri-building-4-line" />{isRTL ? 'مطوّر عقاري' : 'Developer'}
                    </span>
                  </div>
                  <div className={`flex items-center flex-wrap gap-3 mt-2.5 text-xs text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {developer.totalReviews > 0 && (
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400" />
                        <span className="font-semibold text-slate-700">{developer.avgRating.toFixed(1)}</span>
                        <span>({developer.totalReviews})</span>
                      </span>
                    )}
                    {city && (
                      <span className="flex items-center gap-1"><i className="ri-map-pin-2-line" />{city}</span>
                    )}
                    {developer.foundedYear && (
                      <span className="flex items-center gap-1"><i className="ri-calendar-line" />{isRTL ? `تأسست ${developer.foundedYear}` : `Est. ${developer.foundedYear}`}</span>
                    )}
                  </div>

                  {/* FAL license verification line */}
                  {(fal?.licenseNumber || developer.licenseNumber) && (
                    <div className={`mt-2 flex items-center flex-wrap gap-2 text-xs ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium border ${
                        developer.falVerified
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200'
                      }`}>
                        <i className={developer.falVerified ? 'ri-shield-check-fill' : 'ri-shield-line'} />
                        {isRTL ? 'رخصة فال' : 'FAL license'}: {fal?.licenseNumber || developer.licenseNumber}
                      </span>
                      {fal?.status && (
                        <span className="text-slate-400">
                          {fal.statusFullAr || fal.status}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Contact actions */}
                <div className={`flex items-center gap-2 shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {developer.phone && (
                    <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#004D40] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm">
                      <i className="ri-whatsapp-line" /><span className="hidden sm:inline">{isRTL ? 'واتساب' : 'WhatsApp'}</span>
                    </a>
                  )}
                  {developer.phone && (
                    <a href={`tel:${developer.phone}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
                      <i className="ri-phone-line" /><span className="hidden sm:inline">{isRTL ? 'اتصال' : 'Call'}</span>
                    </a>
                  )}
                  {developer.email && (
                    <a href={`mailto:${developer.email}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
                      <i className="ri-mail-line" /><span className="hidden sm:inline">{isRTL ? 'إيميل' : 'Email'}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {stats.map((s) => (
              <div key={s.label} className={`rounded-2xl bg-white border border-slate-200/80 p-4 ${isRTL ? 'text-right' : ''}`}>
                <div className={`flex items-center gap-2 text-xs text-slate-400 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <i className={s.icon} />{s.label}
                </div>
                <div className="text-xl font-semibold text-slate-900">{s.value}</div>
              </div>
            ))}
          </section>

          {description && <p className={`text-gray-600 mb-6 leading-relaxed ${isRTL ? 'text-right' : ''}`}>{description}</p>}

          {/* Projects */}
          {projects.length > 0 && (
            <section className="mb-8">
              <h2 className={`text-xl font-bold text-[var(--pv-color-primary)] mb-4 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'المشاريع' : 'Projects'}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {projects.map((p) => (
                  <DeveloperProjectCard key={p.id} project={p} isRTL={isRTL} />
                ))}
              </div>
            </section>
          )}

          {/* Listings */}
          <h2 className={`text-xl font-bold text-[var(--pv-color-primary)] mb-4 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'الوحدات المعروضة' : 'Available units'}</h2>
          {listingsLoading ? (
            <div className="flex justify-center py-12"><GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" /></div>
          ) : listings.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200 text-gray-500">
              {isRTL ? 'لا توجد وحدات نشطة' : 'No active units'}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {listings.map((p, index) => (
                <ResultCard key={p.id} property={p} eagerImage={index < 3} onSelect={(item) => navigate(`/property/${item.id}`)} />
              ))}
            </div>
          )}

          {/* Pager */}
          {!listingsLoading && totalPages > 1 && (
            <div className={`mt-8 flex flex-wrap items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button onClick={() => goToPage(page - 1)} disabled={page <= 1} className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">{isRTL ? 'السابق' : 'Prev'}</button>
              {(() => {
                const nums: number[] = [];
                const start = Math.max(1, page - 2);
                const end = Math.min(totalPages, page + 2);
                for (let i = start; i <= end; i++) nums.push(i);
                return nums.map((n) => (
                  <button key={n} onClick={() => goToPage(n)} className={`min-w-[2.5rem] px-3 py-2 rounded-lg border text-sm font-medium ${n === page ? 'bg-[#004D40] border-[#004D40] text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{n}</button>
                ));
              })()}
              <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages} className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">{isRTL ? 'التالي' : 'Next'}</button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
